import { OverreadAnalysisSet, SessionNote } from "../../misc/api/apiTypes";
import { ParameterTypeName } from "../../misc/api/ParameterTypes";

export interface PFTOverreading {
    ProtocolNumber: string;
    SiteId: string;
    SessionId: string;
    Overreading: OverreadAnalysisSet;
    Notes: Array<SessionNote>;
    Technician: string
    CurrentTechnicianNoteId: string;
}

export interface Parameter {
    Id?: number;
    DecimalPlaces: number;
    ShowPositiveValueOnly: boolean;
    IsFlowParameter?: boolean;
    IsQualityParameter?: boolean;
    MaxValue?: number;
    MinValue?: number;
    ParameterType?: ParameterTypeName;
    RoundedValue?: number;
    Value: number;
    ValueProvided?: boolean;
    Identifier: string;
    DisplayName: string;
    PercentageOfChange?: number;
}

export interface VolumetricParameter extends Parameter {
    BaseUnits?: VolumeUnit;
    Units?: VolumeUnit;
}



export interface FlowParameter extends Parameter {
    BaseUnits?: FlowUnit;
    Units?: FlowUnit;
}

export interface DiffusingCapacityParameter extends Parameter {
    BaseUnits?: DiffusingCapacityUnit;
    Units?: DiffusingCapacityUnit;
}

export interface PressureParameter extends Parameter {
    BaseUnits?: PressureUnit;
    Units?: PressureUnit;
}

export enum VolumeUnit {
    Litres = "Litres",
    Millilitres = "Millilitres",
}

export enum FlowUnit {
    LitresPerSecond = "LitresPerSecond",
    LitresPerMinute = "LitresPerMinute",
}

export enum DiffusingCapacityUnit {
    DlcoTraditional = "Traditional",
    DlcoSI = "SI",
}

export enum PressureUnit {
    CentimeterOfWater = "CentimeterOfWater"
}

export interface PFTResults {
    SessionId: string;
    AnalysisSetId: string;
    TestResults: Array<TestResults>;
    BestParameters: Parameters;
    ParametersAverages: Parameters;
    ParametersCvs: Parameters;
    DisplayParameters: Parameters;
    DisplayRepeatabilityParameters: Parameters;
    SoftwareInterpretation?: string;
}

export interface SpirometryResults extends PFTResults {
    TestResults: Array<SpirometryTestResults>;
}

export interface SvcResults extends PFTResults {
    TestResults: Array<SvcTestResults>;
}

export interface PressureResults extends PFTResults {
    TestResults: Array<PressureTestResults>;
}

export interface DlcoResults extends PFTResults {
    Attachments: Array<DlcoLungVolumeFile>;
}

export interface OscillometryResults extends PFTResults {
    Attachments: Array<OscillometryFile>;
}


/* This is a copy of api/v3 "File" but capitalised as it comes from the older API */
export interface DlcoLungVolumeFile {
    Id?: string;
    FileName?: string | undefined;
    FileSizeInBytes?: number | undefined;
    FullFilePath?: string | undefined;
    ContentType?: string | undefined;
    DownloadLink?: string | undefined;
}

export interface OscillometryFile {
    Id?: string;
    FileName?: string | undefined;
    FileSizeInBytes?: number | undefined;
    FullFilePath?: string | undefined;
    ContentType?: string | undefined;
    DownloadLink?: string | undefined;
}

export interface LungVolumeResults extends PFTResults {
    Attachments: Array<DlcoLungVolumeFile>;
}

export interface TestRepeatabilityResults {
    TestId: string;
    RepeatabilityParameters: Parameters;
}

export interface TechnicianFixedCommentEntry {
    Id: string;
    IsSelected: boolean;
    FollowOnComment?: string;
}

export interface TestResults extends TestRepeatabilityResults {
    AnalysisSetId?: number;
    TestNumber: number;
    TestDateTime: string;
    TestParameters: Parameters;
    TechnicianAcceptability?: boolean;
    OverreaderAcceptability?: boolean;
}

export interface SpirometryTestResults extends TestResults {
    StartOfTestQuality: ArtifactAcceptability;
    EofeQuality: ArtifactAcceptability;
    TestDurationQuality: ArtifactAcceptability;
    FlowPlateauQuality: ArtifactAcceptability;
    CoughFreeQuality: ArtifactAcceptability;
    FivcFvcQuality: ArtifactAcceptability;
    Fev1Quality: ArtifactAcceptability;
    FvcQuality: ArtifactAcceptability;
}

export interface SvcTestResults extends TestResults {
    FlowPlateauQuality: ArtifactAcceptability;
}

export interface PressureTestResults extends TestResults {
    ShortDurationQuality: ArtifactAcceptability;
}

export interface DlcoTestAcceptabilitySummary {
    TestId: string;
    Accepted: boolean;
    Dlco: number;
    DlcoCorr: number;
    BHT: number;
    IV: number;
    KCO: number;
    VA: number;
}

export interface LungVolumeTestAcceptabilitySummary {
    TestId: string;
    Accepted: boolean;
    Frc: number;
    Tlc: number;
    Erv: number;
    Rv: number;
    Vc: number;
    Ic: number;
    RvTlc: number;
    Cev: number;
    Lci: number;
}

export interface MipTestAcceptabilitySummary {
    TestId: string;
    Accepted: boolean;
    Mip: number;
    Pip: number;
}

export interface MepTestAcceptabilitySummary {
    TestId: string;
    Accepted: boolean;
    Mep: number;
    Pep: number;
}

export interface SnipTestAcceptabilitySummary {
    TestId: string;
    Accepted: boolean;
    Snip: number;
}

export type Parameters = Array<VolumetricParameter | FlowParameter | DiffusingCapacityParameter | PressureParameter>;

export interface StudyRelevantParameters {
    DefaultAnalysisParameters: Parameters;
    AnalysisParameters: Parameters;
    DefaultRepeatabilityParameters: Parameters;
    RepeatabilityParameters: Parameters;
}


export interface StudyFvcRelevantParameters {
    DefaultAnalysisParameters: Parameters;
    AnalysisParameters: Parameters;
    DefaultRepeatabilityParameters: Parameters;
    RepeatabilityParameters: Parameters;
}

export interface StudySvcRelevantParameters {
    DefaultAnalysisParameters: Parameters;
    AnalysisParameters: Parameters;
    DefaultRepeatabilityParameters: Parameters;
    RepeatabilityParameters: Parameters;
}


export interface StudyLungVolumeRelevantParameters {
    DefaultAnalysisParameters: Parameters;
    AnalysisParameters: Parameters;
    DefaultRepeatabilityParameters: Parameters;
    RepeatabilityParameters: Parameters;
}

export interface FvcTestRepeatabilitySummary {
    TestId: string;
    Accepted: boolean;
    Fvc: number;
    Fev1: number;
    Pef: number;
    Fef2575: number;
    PefSec: number;
}

export interface FvcParameters {
    Fvc: number;
    Fev1: number;
    Fev6: number;
    Tv: number;
    Fivc: number;
    PefMin: number;
    PefSec: number;
    PifMin: number;
    PifSec: number;
    Fev3: number;
    Fev05: number;
    Fev075: number;
    Fiv1: number;
    Mvvind: number;
    Ivc: number;
    FefMax: number;
    Fev1Vc: number;
    Fev1Ht2: number;
    Fev3Vc: number;
    Tlc: number;
    Rv: number;
    Frc: number;
    Fev1Q: number;
    Fef2575: number;
    Fef0212: number;
    Fef7585: number;
    Fef25: number;
    Fef50: number;
    Fef75: number;
    Fmft: number;
    Fev1Fev6: number;
    Fev1Fvc: number;
    Fev1R: number;
    Fev05Fvc: number;
    Fev075Fvc: number;
    Fev3Fvc: number;
    Fev6Fvc: number;
    Fef2575Fvc: number;
    FivcFvc: number;
    Fev1Pef: number;
    Fif50Fef50: number;
    Fev1Fivc: number;
    Fiv1Fivc: number;
    Fiv1Fvc: number;
    Fef50Fif50: number;
    Fev1Ivc: number;
    Fev3Fev6: number;
    Ic: number;
    Irv: number;
    Erv: number;
    Fet: number;
    Fif25: number;
    Fif50: number;
    Fif75: number;
    Rawind: number;
}

export interface FvcTestAcceptabilitySummary extends FvcParameters {
    TestId: string;
    TestNumber: number;
    Accepted: boolean;
    Fev1Accepted?: string;
    FvcAccepted?: string;
}

export interface FvcParameters {
    Vc: number;
    Ic: number;
    Ivc: number;
    Frc: number;
    Tv: number;
    Rv: number;
    Tlc: number;
    Erv: number;
    Evc: number;
}

export interface SvcTestAcceptabilitySummary extends FvcParameters {
    TestId: string;
    Accepted: boolean;
}

export interface SvcTestRepeatabilitySummary {
    Accepted: boolean;
    TestId: string;
    Vc: number;
    Ic: number;
}

export enum ArtifactAcceptability {
    Undetermined = "Undetermined",
    Acceptable = "Acceptable",
    Usable = "Usable",
    NotAcceptable = "NotAcceptable"
}

export enum ArtifactAcceptabilityIds {
    Undetermined = "ART_UNDETERMINED",
    Acceptable = "ART_ACCEPTABLE",
    Usable = "ART_USABLE",
    NotAcceptable = "ART_NOTACCEPTABLE"
}

export interface OscillometryTestAcceptabilitySummary {
    Accepted: boolean;
    Ax: string;
    R5: string;
    R20: string;
    R5R20: string;
    R7: string;
    R7R20: string;
}

export interface OscillometryTestRepeatabilitySummary extends OscillometryTestAcceptabilitySummary {
    TestId: string;
}

export interface PefTestRepeatabilitySummary extends PefTestAcceptabilitySummary {
    AnalysisSetId?: string;
    TestId: string;
}

export interface PefTestAcceptabilitySummary {
    PefMin: number;
    PefSec: number;
}
